/**
 * Database extractors + connection-addressed URL/body builders.
 *
 * `extractDbTables` / `extractDbColumns` coerce the upstream
 * `describeDbSchema` payload (where columns come back as either
 * `string[]` or `{name, type?, nullable?}[]` depending on driver) into
 * the typed shapes the DB-query engine UI renders against.
 *
 * Schema / execute hit `GET|POST /db/connections/{connectionId}/…`
 * (graph `:Database` URN). Transport is resolved server-side.
 *
 * Pure functions, no RTK / no Ignition driver — trivially unit-testable.
 */

import { toIntCount } from '../graph-row'
import type {
  PlantDbColumn,
  PlantDbExecuteArgs,
  PlantDbExecuteResult,
  PlantDbQueryType,
  PlantDbSchemaArgs,
  PlantDbSchemaResponse,
  PlantDbTable
} from '../types'

/**
 * Normalize a table's column list into `PlantDbColumn`s. Ignition
 * WebDev returns columns in two shapes depending on driver: either
 * an array of strings (just names) or an array of `{name, type?,
 * nullable?}` maps. Handle both.
 */
function extractDbColumns(value: unknown): PlantDbColumn[] {
  if (!Array.isArray(value)) return []
  const out: PlantDbColumn[] = []
  for (const item of value) {
    if (typeof item === 'string' && item.length > 0) {
      out.push({ name: item })
      continue
    }
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const name = typeof obj['name'] === 'string' ? obj['name'].trim() : ''
    if (!name) continue
    const type = typeof obj['type'] === 'string' ? obj['type'] : undefined
    const nullable = typeof obj['nullable'] === 'boolean' ? obj['nullable'] : undefined
    out.push({ name, type, nullable })
  }
  return out
}

/**
 * Narrow Ignition WebDev's `describeDbSchema` `tables` payload (an
 * untyped array of `{name, schema?, columns}` maps) into typed
 * `PlantDbTable`s. Skips entries without a name; preserves schema
 * when present.
 */
export function extractDbTables(value: unknown): PlantDbTable[] {
  if (!Array.isArray(value)) return []
  const out: PlantDbTable[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const name = typeof obj['name'] === 'string' ? obj['name'].trim() : ''
    if (!name) continue
    const schema =
      typeof obj['schema'] === 'string' && obj['schema'].length > 0 ? obj['schema'] : undefined
    out.push({ name, schema, columns: extractDbColumns(obj['columns']) })
  }
  return out
}

/** URL-encode a graph `:Database` URN for a `/db/connections/{id}/…` path. */
export function encodePlantDbConnectionIdPath(connectionId: string): string {
  return encodeURIComponent(connectionId.trim())
}

/** `GET /db/connections/{id}/schema` (+ optional `?schema=`). */
export function buildPlantDbSchemaUrl(args: PlantDbSchemaArgs): string {
  const id = encodePlantDbConnectionIdPath(args.connectionId)
  const base = `/db/connections/${id}/schema`
  const schema = args.schema?.trim()
  if (schema) {
    return `${base}?schema=${encodeURIComponent(schema)}`
  }
  return base
}

/** `POST /db/connections/{id}/query`. */
export function buildPlantDbQueryUrl(connectionId: string): string {
  return `/db/connections/${encodePlantDbConnectionIdPath(connectionId)}/query`
}

/** Body for `POST …/query` (`query` / `args` aliases accepted by the proxy). */
export function buildPlantDbQueryBody(
  args: Pick<PlantDbExecuteArgs, 'query' | 'args'>,
  queryType: PlantDbQueryType
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    query: args.query,
    query_type: queryType
  }
  if (Array.isArray(args.args) && args.args.length > 0) {
    body.args = args.args
  }
  return body
}

/**
 * Project the raw `describeDbSchema` response into a typed
 * {@link PlantDbSchemaResponse}. Tolerates missing fields by
 * defaulting `database` to the empty string and `schema` to `null`
 * so the UI doesn't crash on a malformed upstream payload.
 */
export function mapPlantDbSchema(raw: unknown): PlantDbSchemaResponse {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  return {
    database: typeof obj['database'] === 'string' ? obj['database'] : '',
    schema: typeof obj['schema'] === 'string' ? obj['schema'] : null,
    tables: extractDbTables(obj['tables']),
    tableCount: toIntCount(obj['tableCount'])
  }
}

/**
 * Project the raw `execute_db_query` response into a typed
 * {@link PlantDbExecuteResult}. `result` stays `unknown` since its
 * shape varies by query-type (rows vs update-count) and is narrowed
 * at render time by the UI's `parseDbResult`.
 */
export function mapPlantDbExecuteResult(raw: unknown): PlantDbExecuteResult {
  if (raw && typeof raw === 'object') {
    return { result: (raw as Record<string, unknown>)['result'] }
  }
  return { result: null }
}
